import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavProps {
  transparentOverHero?: boolean;
  ctaHref?: string;
}

const Nav: React.FC<NavProps> = ({ transparentOverHero = false, ctaHref = '/app' }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!transparentOverHero) return;
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [transparentOverHero]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  const isSolid = !transparentOverHero || scrolled;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 px-3 md:px-6 pt-3 md:pt-5">
        <div
          className={`max-w-5xl mx-auto rounded-full transition-all duration-300 ${
            isSolid ? 'bg-white shadow-[0_8px_32px_rgba(24,16,26,0.10)]' : 'bg-transparent'
          }`}
        >
          <div className="h-14 md:h-16 flex items-center justify-between pl-5 md:pl-8 pr-2 md:pr-3">
            <div className="flex items-center gap-8 md:gap-10">
              <Link to="/" className="flex items-center gap-3" aria-label="DINK Social home">
                <img
                  src={isSolid ? '/DINKSocial_logo_plum.svg' : '/DINKSocial_logo_white.svg'}
                  alt="DINK Social"
                  className="h-7 w-auto"
                />
              </Link>

              <Link
                to="/about"
                className={`hidden md:inline text-sm font-medium tracking-wide transition-colors ${
                  isSolid ? 'text-ink/70 hover:text-ink' : 'text-white/90 hover:text-white'
                }`}
              >
                Our Story
              </Link>
              <Link
                to="/blog"
                className={`hidden md:inline text-sm font-medium tracking-wide transition-colors ${
                  isSolid ? 'text-ink/70 hover:text-ink' : 'text-white/90 hover:text-white'
                }`}
              >
                Dink Journal
              </Link>
            </div>

            <div className="flex items-center gap-1 md:gap-3">
              <a
                href={ctaHref}
                className="text-sm font-semibold px-5 py-2 rounded-full bg-primary text-white hover:bg-primary-dark transition-colors"
              >
                Get the app
              </a>
              <button
                type="button"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
                onClick={() => setMenuOpen((v) => !v)}
                className={`md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors ${
                  isSolid ? 'text-ink hover:bg-ink/5' : 'text-white hover:bg-white/10'
                }`}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  {menuOpen ? (
                    <path
                      d="M5 5l10 10M15 5L5 15"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                    />
                  ) : (
                    <>
                      <path d="M3 5h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                      <path d="M3 10h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                      <path d="M3 15h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        className={`md:hidden fixed inset-0 z-40 bg-primary transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col h-full pt-24 pb-10 px-8">
          <nav className="flex flex-col gap-7" aria-label="Mobile primary">
            <Link
              to="/"
              className="font-display font-medium text-[32px] leading-tight text-white/95 hover:text-white"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="font-display font-medium text-[32px] leading-tight text-white/95 hover:text-white"
            >
              Our Story
            </Link>
            <Link
              to="/blog"
              className="font-display font-medium text-[32px] leading-tight text-white/95 hover:text-white"
            >
              Dink Journal
            </Link>
            <Link
              to="/contact"
              className="font-display font-medium text-[32px] leading-tight text-white/95 hover:text-white"
            >
              Contact
            </Link>
          </nav>

          <div className="mt-auto">
            <a
              href={ctaHref}
              className="inline-flex items-center justify-center w-full text-base font-semibold px-6 py-4 rounded-full bg-white text-primary hover:bg-white/90 transition-colors"
            >
              Get the app
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Nav;
